<template>
  <div class="hm-g">
    <div v-if="loading" class="hm-g__meta">Loading guests…</div>
    <template v-else-if="guests.length">
      <div class="hm-g__head hm-g__grid">
        <div></div>
        <div>Full Name</div>
        <div>Date of Birth</div>
        <div class="u-center">Age</div>
        <div>Passport</div>
        <div class="u-center">Contacts</div>
        <div>Diet</div>
        <div>Restrictions</div>
        <div class="u-center">Allergies</div>
        <div></div>
      </div>
      <div
        v-for="(g, idx) in guests"
        :key="g.id || idx"
        class="hm-g__row hm-g__grid"
      >
        <div class="hm-g__cell u-bold">{{ idx + 1 }}.</div>

        <div class="hm-g__cell hm-g__name">
        <span class="hm-g__gender">
          <MaleIcon v-if="g.gender && g.gender.code === 'male'"/>
          <FemaleIcon v-else-if="g.gender && g.gender.code === 'female'"/>
        </span>
        <HMTooltip>
          <template #trigger>
            <span
              class="hm-g__flag"
              :class="{ 'hm-g__flag--empty': !(g.citizenship && g.citizenship.code) }"
              :title="g.citizenship && g.citizenship.name ? g.citizenship.name : ''"
            >
              <span
                v-if="g.citizenship && g.citizenship.code"
                :class="`fflag ff-md fflag-${g.citizenship.code}`"
              ></span>
            </span>
          </template>
          <template #content>{{ g.citizenship?.name }}</template>
        </HMTooltip>

          <span class="u-name">
          {{ (
            g.gender && g.gender.greeting
          ) ? g.gender.greeting : '' }} {{ g.full_name || (
            (
              g.first_name || ''
            ) + ' ' + (
              g.last_name || ''
            )
          ) }}
        </span>
        </div>

        <div class="hm-g__cell">{{ formatDate(g.dob) }}</div>
        <div class="hm-g__cell u-center">{{ ageFromDOB(g.dob) }}</div>
        <div class="hm-g__cell">{{ g.passport || '' }}</div>

        <div class="hm-g__cell u-center hm-g__contacts">
          <HMTooltip>
            <template #trigger>
              <span @click="onCopyToClipboard(g.email)" class="hm-g__icon" :class="{'hm-g__gray': !g.email}"><EmailIcon/></span>
            </template>
            <template #content>{{ g.email }}</template>
          </HMTooltip>
          <HMTooltip>
            <template #trigger>
              <span @click="onCopyToClipboard(g.phone)" class="hm-g__icon" :class="{'hm-g__gray': !g.phone}"><PhoneIcon/></span>
            </template>
            <template #content>{{ g.phone }}</template>
          </HMTooltip>
        </div>

        <div class="hm-g__cell hm-g__diet">
          <span>{{ g.food_preference && g.food_preference.name }}</span>
          <VegetarianIcon v-if="isVegetarian(g.food_preference && g.food_preference.name)"/>
        </div>

        <div class="hm-g__cell hm-g__restr">
          <span v-if="g.food_lactose_free" title="Lactose Free"><LactoseFree/></span>
          <span v-if="g.food_gluten_free" title="Gluten Free"><GlutenFree/></span>
          <span v-if="g.food_halal" title="Halal"><HalalIcon/></span>
        </div>

        <div class="hm-g__cell u-center">
        <span
          v-if="g.food_allergy"
          class="hm-g__icon"
          :title="g.food_allergy"
        ><WarningIcon/></span>
        </div>

        <div class="hm-g__cell u-right hm-g__menu-wrap">
          <button @click="toggleGuestMenu(g.id)" type="button" class="hm-g__dots">
            <DotsIcon/>
          </button>
          <div v-if="openGuestMenuId === g.id" class="hm-g__menu">
            <button @click="emitAction('edit', g)" type="button" class="hm-g__menu-btn">Edit</button>
            <button @click="emitAction('delete', g.id)" type="button" class="hm-g__menu-btn hm-g__menu-btn--danger">
              Delete
            </button>
          </div>
        </div>
      </div>
    </template>
    <HMSlug v-else-if="!guests.length">
      <template #img><img src="@/assets/img/hm-guests.png" alt="Guests"></template>
      <template #title>No guests added yet</template>
      <template #text>Guests will appear here once added. </template>
    </HMSlug>
  </div>
</template>

<script>
import DotsIcon from '@/views/hotel-management/reservations/detail/icons/DotsIcon.vue'
import MaleIcon from '@/views/hotel-management/reservations/detail/icons/MaleIcon.vue'
import FemaleIcon from '@/views/hotel-management/reservations/detail/icons/FemaleIcon.vue'
import EmailIcon from '@/views/hotel-management/reservations/detail/icons/EmailIcon.vue'
import PhoneIcon from '@/views/hotel-management/reservations/detail/icons/PhoneIcon.vue'
import VegetarianIcon from '@/views/hotel-management/reservations/detail/icons/VegetarianIcon.vue'
import GlutenFree from '@/views/hotel-management/reservations/detail/icons/GlutenFree.vue'
import LactoseFree from '@/views/hotel-management/reservations/detail/icons/LactoseFree.vue'
import HalalIcon from '@/views/hotel-management/reservations/detail/icons/HalalIcon.vue'
import WarningIcon from '@/views/hotel-management/reservations/detail/icons/WarningIcon.vue'
import HMTooltip from '@/views/hotel-management/reservations/components/HMTooltip.vue'
import HMSlug from '@/views/hotel-management/reservations/components/HMSlug.vue'

export default {
  name: 'HMGuestsTable',
  components: {
    HMSlug,
    HMTooltip,
    DotsIcon,
    MaleIcon,
    FemaleIcon,
    EmailIcon,
    PhoneIcon,
    VegetarianIcon,
    GlutenFree,
    LactoseFree,
    HalalIcon,
    WarningIcon
  },
  props: {
    guests: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false }
  },
  data() {
    return { openGuestMenuId: null }
  },
  mounted() {
    document.addEventListener('click', this.closeGuestMenuOnOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeGuestMenuOnOutside)
  },
  methods: {
    toggleGuestMenu(id) {
      this.openGuestMenuId = this.openGuestMenuId === id ? null : id
    },
    closeGuestMenuOnOutside(e) {
      const inside = e.target.closest?.('.hm-g__menu-wrap, .hm-g__menu')
      if (!inside) this.openGuestMenuId = null
    },
    emitAction(action, g = null) {
      this.$emit(action, g)
      this.openGuestMenuId = null
    },
    isVegetarian(name) {
      return String(name || '').trim().toLowerCase() === 'vegetarian'
    },
    parseISODate(iso) {
      if (!iso) return null
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
      if (!m) return null
      const [, y, mo, d] = m.map(Number)
      return new Date(Date.UTC(y, mo - 1, d))
    },
    formatDate(iso) {
      const d = this.parseISODate(iso)
      if (!d) return ''
      // 12 Aug, 1967
      const s = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).format(d)
      return s.replace(/(\d{2} \w{3}) (\d{4})/, '$1, $2')
    },
    ageFromDOB(iso) {
      const d = this.parseISODate(iso)
      if (!d) return ''
      const now = new Date()
      let age = now.getUTCFullYear() - d.getUTCFullYear()
      const m = now.getUTCMonth() - d.getUTCMonth()
      if (m < 0 || (
        m === 0 && now.getUTCDate() < d.getUTCDate()
      )) age--
      return age >= 0 ? age : ''
    },
    onCopyToClipboard(string) {
      navigator.clipboard.writeText(string)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Copied to clipboard',
          })
        })
        .catch(() => { console.error('Something went wrong') })
    },
  }
}
</script>

<style scoped>
.hm-g {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.hm-g__grid {
  display: grid;
  grid-template-columns:
    44px 1.8fr 1.1fr 70px 1fr 120px 1fr 1.2fr 100px 44px;
  align-items: center;
  column-gap: 16px;
}

.hm-g__head {
  padding: 0 20px 2px 17px;
  color: #666;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: .5px;
  text-transform: uppercase;
}

.hm-g__row {
  position: relative;
  background: #fff;
  border: 1px solid #e9e9e9;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .06);
  padding: 12px 20px 12px 17px;
}

.hm-g__cell {
  color: #000;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 14px;
}

.hm-g__icon {
  line-height: 0;
}

.hm-g__name {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.hm-g__gender {
  width: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.hm-g__flag {
  flex-shrink: 0;
  width: 19px;
  height: 19px;
  border-radius: 50%;
  border: 1px solid #b5b5b5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  background: #fff;
  overflow: hidden;
}

.hm-g__flag--empty {
  background: #efefef;
  border-color: #efefef;
}

.hm-g__contacts {
  display: inline-flex;
  align-items: flex-end;
  gap: 14px;
}

.hm-g__diet {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.hm-g__restr {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.hm-g__dots {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
}

.hm-g__menu-wrap {
  position: relative;
}

.hm-g__menu {
  position: absolute;
  right: 0;
  top: -28px;
  background: #fff;
  border: 1px solid #E9E9E9;
  box-shadow: 0 8px 22px rgba(0, 0, 0, .12);
  border-radius: 12px;
  padding: 8px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 140px;
}

.hm-g__menu-btn {
  height: 34px;
  border: 0;
  background: #fff;
  text-align: left;
  padding: 0 10px;
  border-radius: 8px;
  font-family: 'MazzardSoftH', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #2f2f2f;
  cursor: pointer;
}

.hm-g__menu-btn:hover {
  background: #F4F6F8;
}

.hm-g__menu-btn--danger {
  color: #A64833;
}

.hm-g__meta {
  color: #8A8A8A;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 14px;
  text-align: center;
  padding: 8px 0;
}

.hm-g__gray {
  filter: grayscale(100%);
  pointer-events: none;
}

/* utilities (scoped под таблицу) */
.u-center {
  text-align: center;
}

.u-center.hm-g__contacts {
  justify-content: center;
}

.u-right {
  text-align: right;
}

.u-bold {
  font-family: 'MazzardSoftH', sans-serif;
  font-size: 16px;
  font-weight: 600;
}

.u-name {
  color: #000;
  font-family: 'CoreSansCR', sans-serif;
  font-weight: 500;
}
</style>
